import { z } from 'zod';

// Common validation patterns
export const ValidationPatterns = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^\+?[\d\s\-\(\)]+$/,
  // Allowed special characters: ! @ # $ % ^ & * ( ) _ + - = { } [ ] : ; " ' < > , . ? / | \ ~ `
  strongPassword: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()\-_+={}\[\]:;"'<>,.?\/|\\~`])[A-Za-z\d!@#$%^&*()\-_+={}\[\]:;"'<>,.?\/|\\~`]{8,}$/,
  alphanumeric: /^[a-zA-Z0-9]+$/,
  url: /^https?:\/\/.+/,
};

// Common field schemas
export const CommonSchemas = {
  // Text fields
  requiredString: (fieldName = 'Field') => 
    z.string().trim().min(1, `${fieldName} is required`),
  requiredNumber: (fieldName = 'Number', min, max) => {
    let schema = z.number({ 
      required_error: `${fieldName} is required`,
      invalid_type_error: `${fieldName} must be a number`
    });
    
    if (min !== undefined) {
      schema = schema.min(min, `${fieldName} must be at least ${min}`);
    }
    if (max !== undefined) {
      schema = schema.max(max, `${fieldName} must not exceed ${max}`);
    }
    
    return schema;
  },
  optionalString: () => 
    z.string().nullable().optional(),
    
  stringWithLength: (fieldName = 'Field', min = 1, max = 255) => {
    const minWord = min === 1 ? 'character' : 'characters';
    const maxWord = max === 1 ? 'character' : 'characters';
    return z.string()
      .trim()
      .min(min, `${fieldName} must be at least ${min} ${minWord}`)
      .max(max, `${fieldName} must not exceed ${max} ${maxWord}`);
  },
  requiredArray: (fieldName = 'Field') =>
    z.array(z.string()).min(1, `${fieldName} is required`),
  // Email
  email: (required = true) => {
    const schema = z.string().email('Please enter a valid email address');
    return required ? schema : schema.nullable().optional().or(z.literal(''));
  },

  // Password
  password: (required = true) => {
    const schema = z.string()
      .min(8, 'Password must be at least 8 characters')
      .regex(ValidationPatterns.strongPassword,
        'Password must contain uppercase, lowercase, a number, and a special character (e.g. @, #, $, !)'
      );
    return required ? schema : schema.nullable().optional();
  },

  // Phone
  phone: (required = true) => {
    const schema = z.string()
      .regex(ValidationPatterns.phone, 'Please enter a valid phone number');
    return required ? schema : schema.nullable().optional().or(z.literal(''));
  },

  // URL
  url: (required = true) => {
    const schema = z.string()
      .url('Please enter a valid URL');
    return required ? schema : schema.nullable().optional().or(z.literal(''));
  },

  // Numbers
  number: (fieldName = 'Number', min, max) => {
    let schema = z.number({ 
      required_error: `${fieldName} is required`,
      invalid_type_error: `${fieldName} must be a number`
    });
    
    if (min !== undefined) {
      schema = schema.min(min, `${fieldName} must be at least ${min}`);
    }
    if (max !== undefined) {
      schema = schema.max(max, `${fieldName} must not exceed ${max}`);
    }
    
    return schema;
  },

  optionalNumber: (min, max) => {
    let schema = z.number().nullable().optional();
    
    if (min !== undefined || max !== undefined) {
      schema = schema.refine((val) => {
        if (val === undefined || val === null) return true;
        if (min !== undefined && val < min) return false;
        if (max !== undefined && val > max) return false;
        return true;
      }, {
        message: `Number must be between ${min || 'any'} and ${max || 'any'}`
      });
    }
    
    return schema;
  },

  // Boolean
  boolean: (required = false) => {
    return required 
      ? z.boolean({ required_error: 'This field is required' })
      : z.boolean().nullable().optional();
  },

  // Date
  date: (required = true, futureOnly = false, pastOnly = false) => {
    let schema = z.date({ 
      required_error: 'Date is required',
      invalid_type_error: 'Please enter a valid date'
    });

    if (futureOnly) {
      schema = schema.refine(date => date > new Date(), {
        message: 'Date must be in the future'
      });
    }

    if (pastOnly) {
      schema = schema.refine(date => date < new Date(), {
        message: 'Date must be in the past'
      });
    }

    return required ? schema : schema.nullable().optional();
  },

  // Select/Options
  select: (options = [], fieldName = 'Selection') => {
    if (options.length === 0) {
      return z.string().min(1, `${fieldName} is required`);
    }
    
    const validOptions = options.map(opt => 
      typeof opt === 'string' ? opt : opt.value
    );
    
    return z.enum(validOptions, {
      required_error: `${fieldName} is required`,
      invalid_type_error: `Please select a valid ${fieldName.toLowerCase()}`
    });
  },

  // Multi-select
  multiSelect: (options = [], fieldName = 'Selections', minSelections = 0, maxSelections) => {
    const validOptions = options.map(opt => 
      typeof opt === 'string' ? opt : opt.value
    );
    
    let schema = z.array(z.enum(validOptions))
      .min(minSelections, `Please select at least ${minSelections} ${fieldName.toLowerCase()}`);
    
    if (maxSelections) {
      schema = schema.max(maxSelections, `Please select no more than ${maxSelections} ${fieldName.toLowerCase()}`);
    }
    
    return schema;
  },

  // File
  file: (required = true, maxSize = 5 * 1024 * 1024, allowedTypes = []) => {
    let schema = z.instanceof(File, { message: 'Please select a file' });
    
    if (maxSize) {
      schema = schema.refine(file => file.size <= maxSize, {
        message: `File size must be less than ${Math.round(maxSize / 1024 / 1024)}MB`
      });
    }
    
    if (allowedTypes.length > 0) {
      schema = schema.refine(file => allowedTypes.includes(file.type), {
        message: `File type must be one of: ${allowedTypes.join(', ')}`
      });
    }
    
    return required ? schema : schema.nullable().optional();
  }
};

// Pre-built form schemas for common use cases
export const FormSchemas = {
  automationForm: z.object({
    instructionSet: CommonSchemas.requiredString('instruction Set is required'),
  }),
  // User Registration
  userRegistration: z.object({
    firstName: CommonSchemas.stringWithLength('First name', 2, 50),
    lastName: CommonSchemas.stringWithLength('Last name', 2, 50),
    email: CommonSchemas.email(true),
    password: CommonSchemas.password(true),
    confirmPassword: z.string(),
    phone: CommonSchemas.phone(false),
    agreeToTerms: z.boolean().refine(val => val === true, {
      message: 'You must agree to the terms and conditions'
    })
  }).refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword']
  }),

  // User Login
  userLogin: z.object({
    email: CommonSchemas.email(true),
    password: z.string().min(1, 'Password is required'),
    rememberMe: CommonSchemas.boolean(false)
  }),

  // MFA email OTP (login second factor)
  mfaOtp: z.object({
    otp: z
      .string()
      .min(1, 'Verification code is required')
      .regex(/^\d{6}$/, 'Enter the 6-digit code from your email'),
  }),

  // Contact Form
  contactForm: z.object({
    name: CommonSchemas.stringWithLength('Name', 2, 100),
    email: CommonSchemas.email(true),
    subject: CommonSchemas.stringWithLength('Subject', 5, 200),
    message: CommonSchemas.stringWithLength('Message', 10, 1000),
    contactMethod: CommonSchemas.select(['email', 'phone'], 'Preferred contact method')
  }),

  // User Creation Form
  addUserToProject: z.object({
    user: CommonSchemas.requiredString('User'),
    role: z.array(z.union([z.string(), z.number()])).min(1, 'Role is required'),
  }),

  userCreation: z.object({
    username: CommonSchemas.stringWithLength('Username', 3, 100),
    email: CommonSchemas.email(true),
    role:  z.any().refine((val) => val !== null && val !== undefined && val !== '', {
      message: "Role is required",
    }),    
    organizationId: z.any().nullable().optional(),
  }),

  addToTestGroup: z.object({
    testGroupId: CommonSchemas.requiredString('Test Group'),
  }),

  quickCreateTestCase: z.object({
    title: CommonSchemas.stringWithLength('Title', 1, 200),
    module_id: z.union([z.string(), z.number()]).refine(
      (val) => val !== null && val !== undefined && val !== '',
      { message: 'Module is required' }
    ),
  }),

  llmConfiguration: z.object({
    name: CommonSchemas.stringWithLength('Model name', 1, 255),
    modelname: z.any().refine((val) => val !== null && val !== undefined && val !== '', {
      message: "Model Name is required",
    }),    
    modeltype: z.any().refine((val) => val !== null && val !== undefined && val !== '', {
      message: "Model Type is required",
    }),
    // endpointURL: z.any().refine((val) => val !== null && val !== undefined && val !== '', {
    //   message: "Endpoint URL is required",
    // }),    
    apiKey: z.string()
      .transform((val) => val?.trim())
      .refine((val) => val && val.length > 0, {
        message: "API Key is required",
      }),
    status: z.any().refine((val) => val !== null && val !== undefined && val !== '', {
      message: "Status is required",
    }),
    is_default: CommonSchemas.boolean(false) 
  }).superRefine((data, ctx) => { 
    if (data.modelname?.toLowerCase() === 'anthropic' && data.apiKey) {
      const trimmed = typeof data.apiKey === 'string' ? data.apiKey.trim() : '';
      if (trimmed && trimmed !== '••••••••' && !trimmed.startsWith('sk-ant-')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Anthropic API keys must start with sk-ant-',
          path: ['apiKey'],
        });
      }
    }
  }),

  // Profile Update
  profileUpdate: z.object({
    firstName: CommonSchemas.stringWithLength('First name', 2, 50),
    lastName: CommonSchemas.stringWithLength('Last name', 2, 50),
    email: CommonSchemas.email(true),
    phone: CommonSchemas.phone(false),
    bio: z.string().max(500, 'Bio must not exceed 500 characters').nullable().optional(),
    website: CommonSchemas.url(false),
    dateOfBirth: CommonSchemas.date(false, false, true),
    notifications: CommonSchemas.boolean(false)
  }),

  // Project Creation
  projectCreation: z.object({
    name: CommonSchemas.stringWithLength('Project name', 3, 100),
    description: CommonSchemas.stringWithLength('Description', 10, 1000),
    category: CommonSchemas.select([], 'Category'),
    priority: CommonSchemas.select(['low', 'medium', 'high'], 'Priority'),
    startDate: CommonSchemas.date(true, true, false),
    endDate: CommonSchemas.date(false, true, false),
    budget: CommonSchemas.optionalNumber(0, 1000000),
    isPublic: CommonSchemas.boolean(false)
  }).refine(data => {
    if (data.endDate && data.startDate) {
      return data.endDate > data.startDate;
    }
    return true;
  }, {
    message: 'End date must be after start date',
    path: ['endDate']
  }),

  // Settings Form
  settingsForm: z.object({
    theme: CommonSchemas.select(['light', 'dark', 'auto'], 'Theme'),
    language: CommonSchemas.select(['en', 'es', 'fr', 'de'], 'Language'),
    timezone: CommonSchemas.requiredString('Timezone'),
    emailNotifications: CommonSchemas.boolean(false),
    pushNotifications: CommonSchemas.boolean(false),
    marketingEmails: CommonSchemas.boolean(false),
    autoSave: CommonSchemas.boolean(false),
    sessionTimeout: CommonSchemas.number('Session timeout', 5, 120)
  }),


  // Project Form
  projectForm: (includePrefix = false) => {
    const baseSchema = {
      projectName: CommonSchemas.stringWithLength('Project name', 1, 100)
        .refine((value) => {
          if (!value) return false;
          const wordCount = value.trim().split(/\s+/).filter(word => word.length > 0).length;
          return wordCount <= 10;
        }, {
          message: 'Project name cannot exceed 10 words'
        }),
      
      projectDescription: z.string().max(1000, 'Description must not exceed 1000 characters').nullable().optional(),
      
      projectContext: z.string().max(1000, 'Context must not exceed 1000 characters').nullable().optional()
    };

    if (includePrefix) {
      baseSchema.prefix = z.string()
        .min(1, 'Key is required')
        .min(2, 'Key must be at least 2 characters')
        .max(4, 'Key cannot exceed 4 characters')
        .refine((value) => {
          if (!value) return false;
          return !/\s/.test(value);
        }, {
          message: 'Key cannot contain spaces'
        });
    }

    return z.object(baseSchema);
  },
  // Module Form
  moduleForm: z.object({
    moduleName: CommonSchemas.requiredString('Module name').max(100, 'Module name must not exceed 100 characters'),
    moduleContext: z.string().max(1000, 'Context must not exceed 1000 characters').nullable().optional(),
  }),

  // Environment Form
  environmentForm: z.object({
    environmentName: CommonSchemas.stringWithLength('Environment name', 1, 100),
    environmentDescription: z.string().max(500, 'Description must not exceed 500 characters').nullable().optional(),
  }),

  // Variable Form
  variableForm: z.object({
    key: CommonSchemas.stringWithLength('Variable key', 1, 100),
    value: z.string().nullable().optional(),
    description: z.string().max(500, 'Description must not exceed 500 characters').nullable().optional(),
    type: CommonSchemas.select(['environment_variable', 'project_variable'], 'Variable type'),
    isSecret: CommonSchemas.boolean(false),
  }).refine((data) => {
    // For project variables, value is required
    if (data.type === 'project_variable') {
      return data.value && data.value.trim().length > 0;
    }
    return true;
  }, {
    message: 'Variable value is required for project variables',
    path: ['value']
  }),

  // Role Form
  roleForm: z.object({
    name: CommonSchemas.stringWithLength('Role name', 1, 100),
    description: z.string().max(1000, 'Description must not exceed 1000 characters').nullable().optional(),
    type: z.string().nullable().optional(),
    scope: z.string().nullable().optional(),
  }),

  // API Token creation (Profile → API Tokens)
  apiTokenCreation: (existingTokenNames = []) => z.object({
    name: CommonSchemas.stringWithLength('Token name', 1, 100),
    expiresInDays: z.coerce.number().int().min(1).max(365),
  }).refine((data) => {
    const trimmedName = data.name?.trim();
    if (!trimmedName) return true;

    const isDuplicate = existingTokenNames.some((tokenName) => tokenName === trimmedName);
    return !isDuplicate;
  }, {
    message: 'An API token with this name already exists. Choose a different name or delete the existing token first.',
    path: ['name'],
  }),

  // Test Group Form
  testGroupForm: (existingTestGroups = [], editMode = false, initialValues = {}) => z.object({
    name: CommonSchemas.stringWithLength('Group name', 1, 100),
    description: z.string().max(500, 'Description must not exceed 500 characters').nullable().optional()
  }).refine((data) => {
    // Check for duplicate names
    const trimmedName = data.name?.trim();
    if (!trimmedName) return true;
    
    const isDuplicate = existingTestGroups.some(group => 
      group.name.toLowerCase() === trimmedName.toLowerCase() && 
      (!editMode || group.id !== initialValues.id)
    );
    
    return !isDuplicate;
  }, {
    message: 'A test group with this name already exists in this project. Please choose a different name.',
    path: ['name'] // This will show the error on the name field
  }),

  // Organization Form
  organizationForm: z.object({
    name: CommonSchemas.stringWithLength('Organization name', 3, 100),
    agentServiceUrl: CommonSchemas.url(false),
    mfaEnabled: z.boolean().default(true),
  }),

  // App Form (Create/Edit)
  appForm: (existingApps = [], editMode = false, initialValues = {}) => z.object({
    name: CommonSchemas.stringWithLength('App name', 1, 10),
    slug_name: CommonSchemas.stringWithLength('Slug name', 1, 10),
    image_url: z.string().max(500, 'Image URL must not exceed 500 characters').nullable().optional().or(z.literal('')),
    description: z.string().max(100, 'Description must not exceed 100 characters').nullable().optional().or(z.literal('')),
  }).refine((data) => {
    // Check for duplicate names
    const trimmedName = data.name?.trim();
    if (!trimmedName) return true;
    
    const isDuplicate = existingApps.some(app => 
      app.name.toLowerCase() === trimmedName.toLowerCase() && 
      (!editMode || app.id !== initialValues.id)
    );
    
    return !isDuplicate;
  }, {
    message: 'An app with this name already exists. Please choose a different name.',
    path: ['name']
  }).refine((data) => {
    // Check for duplicate slug names
    const trimmedSlug = data.slug_name?.trim();
    if (!trimmedSlug) return true;
    
    const isDuplicate = existingApps.some(app => 
      app.slug_name.toLowerCase() === trimmedSlug.toLowerCase() && 
      (!editMode || app.id !== initialValues.id)
    );
    
    return !isDuplicate;
  }, {
    message: 'An app with this slug name already exists. Please choose a different slug name.',
    path: ['slug_name']
  }),

  // Test Launch Form
  testLaunchForm: (testScope = { total: 0, automated: 0 }, options = {}) => z.object({
    launchName: CommonSchemas.stringWithLength('Launch name', 1, 100),
    launchType: z.enum(['AUTOMATED', 'MANUAL', 'COMPLETE']).optional().default('COMPLETE'),
    startType: z.enum(['Immediate', 'Schedule']).optional().default('Immediate'),
    isSequential: z.boolean().optional(),
    continueOnFailure: z.boolean().optional(),
    scheduledTime: z.date().nullable().optional(),
    environment: z.string({
      required_error: 'Please select an Environment',
      invalid_type_error: 'Please select an Environment',
    }).trim().min(1, 'Please select an Environment'),
    // Populated by AIModelRuntimeSelector (concrete JSON or "auto" for server-resolve)
    aiModel: z.string().nullable().optional(),
  }).refine((data) => {
    if (data.startType === 'Schedule') {
      return data.scheduledTime !== undefined && data.scheduledTime !== null;
    }
    return true;
  }, {
    message: 'Scheduled time is required when start type is Schedule',
    path: ['scheduledTime']
  }).refine((data) => {
    if (data.scheduledTime) {
      return data.scheduledTime > new Date();
    }
    return true;
  }, {
    message: 'Scheduled time must be in the future',
    path: ['scheduledTime']
  }).refine((data) => {
    const launchType = data.launchType || options.launchType || 'COMPLETE';
    const needsAiModel = launchType !== 'MANUAL' && (testScope?.automated || 0) > 0;
    if (needsAiModel) {
      return typeof data.aiModel === 'string' && data.aiModel.trim().length > 0;
    }
    return true;
  }, {
    message: 'AI Model is required',
    path: ['aiModel']
  })
};

// Utility functions for dynamic schema creation
export const SchemaUtils = {
  // Create schema from field definitions
  createSchemaFromFields: (fields) => {
    const schemaObject = {};
    
    fields.forEach(field => {
      const { name, type, required = false, validation = {} } = field;
      
      switch (type) {
        case 'email':
          schemaObject[name] = CommonSchemas.email(required);
          break;
        case 'password':
          schemaObject[name] = CommonSchemas.password(required);
          break;
        case 'phone':
          schemaObject[name] = CommonSchemas.phone(required);
          break;
        case 'url':
          schemaObject[name] = CommonSchemas.url(required);
          break;
        case 'number':
          schemaObject[name] = required 
            ? CommonSchemas.number(field.label || name, validation.min, validation.max)
            : CommonSchemas.optionalNumber(validation.min, validation.max);
          break;
        case 'select':
          schemaObject[name] = CommonSchemas.select(field.options || [], field.label || name);
          break;
        case 'checkbox':
        case 'switch':
          schemaObject[name] = CommonSchemas.boolean(required);
          break;
        case 'date':
          schemaObject[name] = CommonSchemas.date(required, validation.futureOnly, validation.pastOnly);
          break;
        default:
          // Text input
          if (required) {
            schemaObject[name] = validation.minLength || validation.maxLength
              ? CommonSchemas.stringWithLength(
                  field.label || name, 
                  validation.minLength || 1, 
                  validation.maxLength || 255
                )
              : CommonSchemas.requiredString(field.label || name);
          } else {
            schemaObject[name] = CommonSchemas.optionalString();
          }
      }
    });
    
    return z.object(schemaObject);
  },

  // Add custom validation to existing schema
  addCustomValidation: (schema, validationFn, errorMessage, path) => {
    return schema.refine(validationFn, {
      message: errorMessage,
      path: path ? [path] : undefined
    });
  },

  // Merge multiple schemas
  mergeSchemas: (...schemas) => {
    return schemas.reduce((merged, schema) => {
      return merged.merge(schema);
    }, z.object({}));
  }
};

export default {
  ValidationPatterns,
  CommonSchemas,
  FormSchemas,
  SchemaUtils
};
